import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { ChatBubbleLeftRightIcon, EyeIcon } from '@heroicons/react/24/outline';

import { PageContainer, Spinner } from '@shared';

import { MDENTURE_PRODUCTION_CENTER_ID } from '@/config/mdenture';
import { useAuth } from '@/features/auth/useAuth';
import { useLocale, useTranslations } from '@/i18n/client';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';
import { OrdersApi } from '@/services/api/orders-api';
import type { OrderFlowState, ProjectOrderListItem } from '@/services/api/types';
import { parseUtcDate } from '@/utils/date-time';
import { isDesignReviewState, isScanRevisionState, isScanRevisionUploadOnlyState } from './order-status';
import { isAbortError } from './orders-page-utils';

interface OrderRow {
  readonly id: string;
  readonly orderNumber: string;
  readonly created: string;
  readonly projectName: string;
  readonly orderState: string;
  readonly orderStateId: string | null;
  readonly orderStepId: string | null;
  readonly unreadMessageCount: number;
  readonly internalCaseId: string;
}

type SortableColumn = 'orderNumber' | 'created' | 'projectName' | 'orderState';
type SortDirection = 'asc' | 'desc';
type PaginationEntry = number | 'ellipsis';
type StatusFilter = 'all' | string;

interface StatusFilterOption {
  readonly key: string;
  readonly label: string;
  readonly sequenceNumber: number;
}

interface StatusFilterData {
  readonly options: readonly StatusFilterOption[];
  readonly labels: ReadonlyMap<string, string>;
  readonly activeOrderStateIds: ReadonlySet<string>;
  readonly designReviewOrderStepIds: ReadonlySet<string>;
  readonly scanRevisionOrderStepIds: ReadonlySet<string>;
}

const CREATED_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const TEXT_COMPARATOR = new Intl.Collator('de', {
  sensitivity: 'base',
  numeric: true,
});

const PAGE_SIZE = 20;
const ONLY_ACTIVE_FILTER = 'only-active';

const FLOW_ORDER_TYPE_KEYS: readonly string[] = ['production', 'designproduction'];

const FALLBACK_ORDERS: readonly OrderRow[] = Array.from({ length: 21 }, (_, index) => {
  const orderNumberValue = index === 0 ? 998 : 1000 + index;
  const createdDay = String(index + 1).padStart(2, '0');

  return {
    id: `00000000-0000-0000-0000-${String(index + 1).padStart(12, '0')}`,
    orderNumber: `ZA-2026-${String(orderNumberValue).padStart(4, '0')}`,
    created: `2026-07-${createdDay}T08:00:00.000Z`,
    projectName:
      index === 5
        ? 'Musterarbeit Schneider'
        : index === 12
          ? 'Musterarbeit Novak'
          : index === 1
            ? 'Implantatfall Huber'
            : index === 0
              ? 'Totalprothese Wimmer'
              : `Fall ${index + 1}`,
    orderState:
      index % 4 === 0
        ? 'Angelegt'
        : index % 4 === 1
          ? 'In Bearbeitung'
          : index % 4 === 2
            ? 'Versendet'
            : 'Abgeschlossen',
    orderStateId: null,
    orderStepId: null,
    unreadMessageCount: index === 1 ? 100 : 0,
    internalCaseId: index === 0 ? 'IC-2044' : index === 5 ? 'IC-1187' : '',
  };
});

function convertOrderRow(order: ProjectOrderListItem): OrderRow {
  return {
    id: order.id,
    orderNumber: order.orderNumber,
    created: order.created,
    projectName: order.projectName,
    orderState: order.orderState,
    orderStateId: order.orderStateId ?? null,
    orderStepId: order.orderStepId ?? null,
    unreadMessageCount: order.unreadMessageCount,
    internalCaseId: order.internalCaseId ?? '',
  };
}

function formatCreated(created: string): string {
  return CREATED_FORMATTER.format(parseUtcDate(created));
}

function getSortIcon(column: SortableColumn, sortBy: SortableColumn, sortDirection: SortDirection): string {
  if (sortBy !== column) {
    return '↕';
  }

  return sortDirection === 'asc' ? '↑' : '↓';
}

function buildPaginationEntries(currentPage: number, totalPages: number): readonly PaginationEntry[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);

  if (currentPage <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }

  if (currentPage >= totalPages - 2) {
    pages.add(totalPages - 1);
    pages.add(totalPages - 2);
    pages.add(totalPages - 3);
  }

  return Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right)
    .flatMap((page, index, array) => {
      const previous = array[index - 1];
      return previous !== undefined && page - previous > 1 ? ['ellipsis', page] : [page];
    });
}

function normalizeStatus(state: string): string {
  return state
    .trim()
    .toLocaleLowerCase('de')
    .replace(/[_\s-]+/g, '');
}

function orderTypeKeyOf(orderType: string | number): string {
  if (typeof orderType === 'number') {
    return orderType === 0 ? 'production' : orderType === 1 ? 'designproduction' : String(orderType);
  }

  const normalized = normalizeStatus(orderType);
  return normalized === '0' ? 'production' : normalized === '1' ? 'designproduction' : normalized;
}

function normalizeId(value: string | null | undefined): string {
  return value?.trim().toLocaleLowerCase('en-US') ?? '';
}

function flowOrderRank(orderTypeKey: string): number {
  const preferredRank = FLOW_ORDER_TYPE_KEYS.indexOf(orderTypeKey);
  return preferredRank >= 0 ? preferredRank : FLOW_ORDER_TYPE_KEYS.length;
}

function flowStatesForList(states: readonly OrderFlowState[]): OrderFlowState[][] {
  const productionCenterId = normalizeId(MDENTURE_PRODUCTION_CENTER_ID);
  const orderTypeKeys = [...new Set(states.map((state) => orderTypeKeyOf(state.orderType)))].sort(
    (left, right) => flowOrderRank(left) - flowOrderRank(right) || left.localeCompare(right),
  );

  return orderTypeKeys.flatMap((orderTypeKey) => {
    const typedStates = states.filter((state) => orderTypeKeyOf(state.orderType) === orderTypeKey);
    const groupStates = typedStates.filter((state) => normalizeId(state.groupID) === productionCenterId);
    const selectedStates =
      groupStates.length > 0 ? groupStates : typedStates.filter((state) => !normalizeId(state.groupID));

    return selectedStates.length > 0 ? [selectedStates] : [];
  });
}

function flowStateLabel(state: OrderFlowState): string {
  return state.displayName?.trim() || state.name.trim();
}

function selectableStepKeys(states: readonly OrderFlowState[]): string[] {
  const statesByStep = new Map<string, OrderFlowState>();

  for (const state of states) {
    if (!state.isEnabled || normalizeStatus(state.name) === 'none') {
      continue;
    }

    const key = normalizeId(state.orderStepId);
    if (!key) {
      continue;
    }

    const existing = statesByStep.get(key);
    if (!existing || state.sequenceNumber > existing.sequenceNumber) {
      statesByStep.set(key, state);
    }
  }

  return [...statesByStep.entries()]
    .sort(
      ([leftKey, leftState], [rightKey, rightState]) =>
        leftState.sequenceNumber - rightState.sequenceNumber || leftKey.localeCompare(rightKey),
    )
    .map(([key]) => key);
}

function mergeFlowStepOrders(flowOrders: readonly (readonly string[])[]): string[] {
  const [primaryOrder = [], ...secondaryOrders] = [...flowOrders].sort(
    (left, right) => right.length - left.length,
  );
  const mergedOrder = [...primaryOrder];

  for (const flowOrder of secondaryOrders) {
    flowOrder.forEach((key, index) => {
      if (mergedOrder.includes(key)) {
        return;
      }

      const nextSharedKey = flowOrder.slice(index + 1).find((candidate) => mergedOrder.includes(candidate));
      const insertAt = nextSharedKey ? mergedOrder.indexOf(nextSharedKey) : mergedOrder.length;

      mergedOrder.splice(insertAt, 0, key);
    });
  }

  return mergedOrder;
}

function buildStatusFilterData(states: readonly OrderFlowState[]): StatusFilterData {
  const options = new Map<string, StatusFilterOption>();
  const labels = new Map<string, string>();
  const activeOrderStateIds = new Set<string>();
  const designReviewOrderStepIds = new Set<string>();
  const scanRevisionOrderStepIds = new Set<string>();
  const flowStates = flowStatesForList(states);
  const selectedStates = flowStates.flat();

  for (const state of states) {
    if (!state.isActive || normalizeStatus(state.name) === 'uploading') {
      continue;
    }

    const key = normalizeId(state.id);
    if (key) {
      activeOrderStateIds.add(key);
    }
  }

  for (const state of selectedStates) {
    const key = normalizeId(state.orderStepId);
    const label = flowStateLabel(state);
    if (!key || !label) {
      continue;
    }

    if (!labels.has(key)) {
      labels.set(key, label);
    }

    if (isDesignReviewState(state.name)) {
      designReviewOrderStepIds.add(key);
    }

    if (isScanRevisionState(state.name)) {
      scanRevisionOrderStepIds.add(key);
    }

    if (!state.isEnabled || normalizeStatus(state.name) === 'none') {
      continue;
    }

    const existing = options.get(key);
    if (!existing || state.sequenceNumber > existing.sequenceNumber) {
      options.set(key, {
        key,
        label,
        sequenceNumber: state.sequenceNumber,
      });
    }
  }
  const orderedKeys = mergeFlowStepOrders(flowStates.map(selectableStepKeys));
  const orderedOptions = orderedKeys.flatMap((key) => {
    const option = options.get(key);
    return option ? [option] : [];
  });
  return {
    options: orderedOptions,
    labels,
    activeOrderStateIds,
    designReviewOrderStepIds,
    scanRevisionOrderStepIds,
  };
}

function matchesOrderFilter(
  order: OrderRow,
  statusFilter: string,
  normalizedSearch: string,
  activeOrderStateIds: ReadonlySet<string>,
): boolean {
  const matchesStatus =
    statusFilter === 'all' ||
    (statusFilter === ONLY_ACTIVE_FILTER
      ? activeOrderStateIds.has(normalizeId(order.orderStateId))
      : normalizeId(order.orderStepId) === statusFilter);

  if (!matchesStatus || normalizedSearch.length === 0) {
    return matchesStatus;
  }

  const searchable = [
    order.orderNumber,
    formatCreated(order.created),
    order.projectName,
    order.internalCaseId,
  ]
    .join(' ')
    .toLocaleLowerCase('de');

  return searchable.includes(normalizedSearch);
}

export function OrdersPage() {
  const t = useTranslations('common.orders');
  const tStatus = useTranslations('common.status');
  const tErrors = useTranslations('errors.api');
  const locale = useLocale();
  const { client } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState<readonly OrderRow[]>(() => (client ? [] : FALLBACK_ORDERS));
  const [flowStates, setFlowStates] = useState<readonly OrderFlowState[]>([]);
  const [isLoading, setIsLoading] = useState(() => Boolean(client));
  const [errorKey, setErrorKey] = useState<ApiErrorKey | null>(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortableColumn>('created');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const statusData = useMemo(() => buildStatusFilterData(flowStates), [flowStates]);

  useEffect(() => {
    if (!client) {
      return;
    }

    let isActive = true;
    const abort = new AbortController();
    const api = new OrdersApi(client);

    async function loadOrders(): Promise<void> {
      setIsLoading(true);
      setErrorKey(null);

      try {
        const [ordersResult, flowStatesResult] = await Promise.allSettled([
          api.getProjectOrders(abort.signal),
          api.getFlowStates(locale, abort.signal),
        ]);

        if (!isActive) {
          return;
        }

        if (ordersResult.status === 'rejected') {
          throw ordersResult.reason;
        }

        setOrders(ordersResult.value.map(convertOrderRow));
        setFlowStates(flowStatesResult.status === 'fulfilled' ? flowStatesResult.value : []);
      } catch (error) {
        if (isActive && !isAbortError(error)) {
          setErrorKey(errorKeyOf(error));
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    void loadOrders();

    return () => {
      isActive = false;
      abort.abort();
    };
  }, [client, locale]);

  function statusLabel(order: OrderRow): string {
    return statusData.labels.get(normalizeId(order.orderStepId)) ?? order.orderState;
  }

  function toggleSort(column: SortableColumn): void {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(column);
    setSortDirection('asc');
  }

  const visibleOrders = useMemo(() => {
    const normalizedSearch = searchText.trim().toLocaleLowerCase('de');

    const filteredOrders = orders.filter((order) =>
      matchesOrderFilter(order, statusFilter, normalizedSearch, statusData.activeOrderStateIds),
    );

    return [...filteredOrders].sort((left, right) => {
      const factor = sortDirection === 'asc' ? 1 : -1;

      if (sortBy === 'created') {
        const leftCreated = Date.parse(left.created);
        const rightCreated = Date.parse(right.created);
        return (leftCreated - rightCreated) * factor;
      }

      if (sortBy === 'orderState') {
        return TEXT_COMPARATOR.compare(statusLabel(left), statusLabel(right)) * factor;
      }

      return TEXT_COMPARATOR.compare(left[sortBy], right[sortBy]) * factor;
    });
  }, [orders, searchText, statusData, statusFilter, sortBy, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(visibleOrders.length / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = (safeCurrentPage - 1) * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, visibleOrders.length);
  const pagedOrders = visibleOrders.slice(pageStart, pageStart + PAGE_SIZE);
  const paginationEntries = useMemo(
    () => buildPaginationEntries(safeCurrentPage, totalPages),
    [safeCurrentPage, totalPages],
  );
  const isOrdersLoading = Boolean(client && isLoading);

  function isReviewRequired(order: OrderRow): boolean {
    return (
      isDesignReviewState(order.orderState) ||
      isScanRevisionState(order.orderState) ||
      statusData.designReviewOrderStepIds.has(normalizeId(order.orderStepId)) ||
      statusData.scanRevisionOrderStepIds.has(normalizeId(order.orderStepId))
    );
  }

  function isScanRevisionRequired(order: OrderRow): boolean {
    return (
      isScanRevisionState(order.orderState) ||
      statusData.scanRevisionOrderStepIds.has(normalizeId(order.orderStepId))
    );
  }

  return (
    <PageContainer className="md-orders-page max-lg:w-full">
      <section
        className="@container mt-6 flex flex-col gap-4 max-lg:mt-4 max-lg:gap-3"
        aria-label={t('title')}
      >
        <div className="md-orders__card flex flex-col gap-5 p-8 max-lg:gap-4 max-lg:p-4">
          <div className="flex flex-wrap items-start justify-between gap-3 max-lg:flex-col">
            <div className="flex flex-col gap-2">
              <h1 className="md-page__title m-0">{t('title')}</h1>
              <p className="md-page__lead m-0 max-w-none">{t('description')}</p>
            </div>
          </div>

          <div className="grid w-full grid-cols-[minmax(0,1fr)_minmax(13rem,16rem)] items-end gap-4 max-lg:grid-cols-1">
            <div className="min-w-0">
              <label htmlFor="orders-search" className="zs-field__label">
                {t('searchLabel')}
              </label>
              <input
                id="orders-search"
                type="search"
                className="zs-field__input w-full min-h-[3.25rem] py-[0.9rem] text-base"
                value={searchText}
                onChange={(event) => {
                  setSearchText(event.target.value);
                  setCurrentPage(1);
                }}
                placeholder={t('searchPlaceholder')}
              />
            </div>

            <div className="min-w-0">
              <label htmlFor="orders-status-filter" className="zs-field__label">
                {t('statusFilterLabel')}
              </label>
              <select
                id="orders-status-filter"
                className="zs-field__input w-full min-h-[3.25rem] py-[0.9rem] text-base"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value as StatusFilter);
                  setCurrentPage(1);
                }}
              >
                <option value="all">{t('statusFilterAll')}</option>
                <option value={ONLY_ACTIVE_FILTER}>{t('statusFilterOnlyActive')}</option>
                {statusData.options.map(({ key, label }) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="md-orders__table-card p-5 @max-[70rem]:p-3">
          <div className="overflow-x-auto @max-[70rem]:overflow-visible">
            {errorKey ? <p className="md-orders__load-error">{tErrors(errorKey)}</p> : null}
            <table className="md-orders__table">
              <colgroup>
                <col className="md-orders__column--order-number" />
                <col className="md-orders__column--created" />
                <col className="md-orders__column--project" />
                <col className="md-orders__column--case-id" />
                <col className="md-orders__column--status" />
                <col className="md-orders__column--review" />
                <col className="md-orders__column--messages" />
                <col className="md-orders__column--open" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">
                    <button
                      type="button"
                      className="md-orders__sort-btn"
                      onClick={() => toggleSort('orderNumber')}
                    >
                      {t('columns.orderNumber')}{' '}
                      <span>{getSortIcon('orderNumber', sortBy, sortDirection)}</span>
                    </button>
                  </th>
                  <th scope="col">
                    <button
                      type="button"
                      className="md-orders__sort-btn"
                      onClick={() => toggleSort('created')}
                    >
                      {t('columns.created')} <span>{getSortIcon('created', sortBy, sortDirection)}</span>
                    </button>
                  </th>
                  <th scope="col">
                    <button
                      type="button"
                      className="md-orders__sort-btn"
                      onClick={() => toggleSort('projectName')}
                    >
                      {t('columns.projectName')}{' '}
                      <span>{getSortIcon('projectName', sortBy, sortDirection)}</span>
                    </button>
                  </th>
                  <th scope="col">{t('columns.internalCaseId')}</th>
                  <th scope="col">
                    <button
                      type="button"
                      className="md-orders__sort-btn"
                      onClick={() => toggleSort('orderState')}
                    >
                      {t('columns.orderState')}{' '}
                      <span>{getSortIcon('orderState', sortBy, sortDirection)}</span>
                    </button>
                  </th>
                  <th scope="col" className="w-[9rem] min-w-[9rem] text-center whitespace-nowrap" />
                  <th scope="col" className="md-orders__messages-column" />
                  <th scope="col" className="md-orders__open-column" />
                </tr>
              </thead>
              <tbody>
                {isOrdersLoading ? (
                  <tr>
                    <td colSpan={8} className="md-orders__empty">
                      <Spinner label={tStatus('loading')} />
                    </td>
                  </tr>
                ) : pagedOrders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="md-orders__empty">
                      {t('empty')}
                    </td>
                  </tr>
                ) : (
                  pagedOrders.map((order) => {
                    const reviewRequired = isReviewRequired(order);
                    const scanRevisionRequired = isScanRevisionRequired(order);
                    const scanUploadRequired = isScanRevisionUploadOnlyState(order.orderState);
                    const actionLabel = scanUploadRequired
                      ? t('scanUploadAction')
                      : scanRevisionRequired
                        ? t('scanRevisionAction')
                        : t('reviewAction');
                    const actionTitle = scanUploadRequired
                      ? t('scanUploadRequiredTitle')
                      : scanRevisionRequired
                        ? t('scanRevisionRequiredTitle')
                        : t('reviewRequiredTitle');
                    const actionDescription = scanUploadRequired
                      ? t('scanUploadRequiredDescription')
                      : scanRevisionRequired
                        ? t('scanRevisionRequiredDescription')
                        : t('reviewRequiredDescription');

                    return (
                      <tr key={order.id}>
                        <td data-label={t('columns.orderNumber')}>{order.orderNumber}</td>
                        <td data-label={t('columns.created')}>{formatCreated(order.created)}</td>
                        <td data-label={t('columns.projectName')}>{order.projectName}</td>
                        <td data-label={t('columns.internalCaseId')}>{order.internalCaseId}</td>
                        <td className="md-orders__open-cell" data-label={t('columns.orderState')}>
                          <div className="md-orders__status-cell">
                            <span
                              className={`md-orders__status md-orders__status--${reviewRequired ? 'review' : 'default'}`}
                            >
                              {statusLabel(order)}
                            </span>
                          </div>
                        </td>
                        <td
                          className={
                            reviewRequired
                              ? 'md-orders__review-cell'
                              : 'w-[9rem] min-w-[9rem] text-center whitespace-nowrap'
                          }
                        >
                          {reviewRequired ? (
                            <Link
                              className="zs-button md-orders__review-notice no-underline"
                              to={`/cases/${order.id}/${scanRevisionRequired ? 'scan-revision' : 'review'}`}
                              aria-label={`${actionLabel} ${order.orderNumber}`}
                              title={actionLabel}
                            >
                              <span className="md-orders__review-icon" aria-hidden="true">
                                <EyeIcon className="block h-5 w-5 shrink-0" />
                              </span>
                              <span className="md-orders__review-copy">
                                <strong>{actionTitle}</strong>
                                <span>{actionDescription}</span>
                              </span>
                            </Link>
                          ) : null}
                        </td>
                        <td className="md-orders__messages-column">
                          {order.unreadMessageCount > 0 ? (
                            <span
                              role="img"
                              className="inline-flex items-center gap-1.5 font-semibold text-[var(--md-primary)]"
                              aria-label={t('unreadMessagesLabel', {
                                count: order.unreadMessageCount > 99 ? '99+' : order.unreadMessageCount,
                              })}
                              title={t('unreadMessagesLabel', {
                                count: order.unreadMessageCount > 99 ? '99+' : order.unreadMessageCount,
                              })}
                            >
                              <ChatBubbleLeftRightIcon className="h-5 w-5" aria-hidden="true" />
                              <span aria-hidden="true">
                                {order.unreadMessageCount > 99 ? '99+' : order.unreadMessageCount}
                              </span>
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="zs-button box-border w-full max-w-full whitespace-nowrap px-[0.9rem] py-[0.42rem] text-sm"
                            aria-label={`${t('openAction')} ${order.orderNumber}`}
                            onClick={() => navigate(`/cases/${order.id}`, { state: { order } })}
                          >
                            {t('openAction')}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {visibleOrders.length > PAGE_SIZE ? (
            <nav
              className="mt-4 flex flex-wrap items-center justify-between gap-4 border-t border-[var(--md-border)] pt-4 max-lg:items-stretch max-lg:flex-col max-lg:gap-3"
              aria-label={t('paginationLabel')}
            >
              <div className="flex flex-col gap-1">
                <div className="md-orders__pagination-info">
                  {t('paginationInfo', { page: safeCurrentPage, totalPages })}
                </div>
                <div className="md-orders__pagination-range">
                  {t('paginationRange', { start: pageStart + 1, end: pageEnd, total: visibleOrders.length })}
                </div>
              </div>
              <div className="ml-auto flex flex-wrap items-center gap-2 max-lg:ml-0 max-lg:grid max-lg:w-full max-lg:grid-cols-2">
                <button
                  type="button"
                  className="zs-button zs-button--secondary md-orders__page-btn min-w-12 px-[0.9rem] max-lg:w-full max-lg:min-w-0 max-lg:px-2"
                  onClick={() => setCurrentPage(1)}
                  disabled={safeCurrentPage <= 1}
                >
                  {t('paginationFirst')}
                </button>
                <button
                  type="button"
                  className="zs-button zs-button--secondary md-orders__page-btn min-w-12 px-[0.9rem] max-lg:w-full max-lg:min-w-0 max-lg:px-2"
                  onClick={() => setCurrentPage(Math.max(1, safeCurrentPage - 1))}
                  disabled={safeCurrentPage <= 1}
                >
                  {t('paginationPrev')}
                </button>
                <div
                  className="flex flex-wrap items-center gap-[0.4rem] max-lg:col-span-full max-lg:justify-center"
                  aria-label={t('paginationPagesLabel')}
                >
                  {paginationEntries.map((entry, index) =>
                    entry === 'ellipsis' ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="inline-flex min-w-6 items-center justify-center font-bold text-[var(--zs-muted)]"
                        aria-hidden="true"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={entry}
                        type="button"
                        className={`zs-button zs-button--secondary md-orders__page-btn min-w-12 px-[0.9rem] max-lg:w-auto max-lg:min-w-11${entry === safeCurrentPage ? ' md-orders__page-btn--current' : ''}`}
                        onClick={() => setCurrentPage(entry)}
                        aria-current={entry === safeCurrentPage ? 'page' : undefined}
                      >
                        {entry}
                      </button>
                    ),
                  )}
                </div>
                <button
                  type="button"
                  className="zs-button zs-button--secondary md-orders__page-btn min-w-12 px-[0.9rem] max-lg:w-full max-lg:min-w-0 max-lg:px-2"
                  onClick={() => setCurrentPage(Math.min(totalPages, safeCurrentPage + 1))}
                  disabled={safeCurrentPage >= totalPages}
                >
                  {t('paginationNext')}
                </button>
                <button
                  type="button"
                  className="zs-button zs-button--secondary md-orders__page-btn min-w-12 px-[0.9rem] max-lg:w-full max-lg:min-w-0 max-lg:px-2"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={safeCurrentPage >= totalPages}
                >
                  {t('paginationLast')}
                </button>
              </div>
            </nav>
          ) : null}
        </div>
      </section>
    </PageContainer>
  );
}
